import { IconButton, Tooltip } from "@husqvarna/ui-core";
import { Grid } from "@husqvarna/ui-core";
import { Box, Typography } from "@husqvarna/ui-core";
import { useState } from "react";
import { FieldError, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { useIntl } from "react-intl";
import { AmountUnit } from "../../models/Enums";
import FormTextWithSuffix from "../../../shared/components/FormTextWithSuffix";
import Toggle from "../../../shared/components/Toggle/Toggle";

import "./ServiceCostPerYear.css";

export interface ServiceYearCostsProps {
  currency: string;
  errors: any;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  costsFieldPath: string;
  unitFieldPath: string;
  disabled?: boolean;
  onUnitChange?: (unit: AmountUnit) => void;
}

const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

const ServiceYearCosts = ({
  currency,
  errors,
  register,
  setValue,
  costsFieldPath,
  unitFieldPath,
  disabled = false,
  onUnitChange,
}: ServiceYearCostsProps) => {
  const [unit, setUnit] = useState<AmountUnit>(AmountUnit.Currency);
  const intl = useIntl();

  const validateServiceCostMax = (value: number | undefined): boolean => {
    if (value && unit === AmountUnit.Percent) return value <= 100;

    return true;
  };

  const getFormFieldPropertiesForServiceCost = () => {
    return {
      disabled,
      min: {
        value: 1,
        message: intl.formatMessage({
          id: "general.formValidation.greaterThanZero",
          defaultMessage: "Needs to be greater than zero",
          description: "Form error message for greater than zero",
        }),
      },
      validate: {
        maxValue: (v: any) =>
          validateServiceCostMax(v) ||
          intl.formatMessage({
            id: "general.formValidation.maxValuePercentageError",
            defaultMessage: "Needs to be less than or equal to 100",
            description: "Form error message for less or equal to 100",
          }),
      },
      pattern: {
        value: /\d+/,
        message: intl.formatMessage({
          id: "general.formValidation.notNumeric",
          defaultMessage: "Needs to be a number",
          description: "Form error message for not a number",
        }),
      },
    };
  };

  const errorsArray = getNestedValue(errors, costsFieldPath);

  return (
    <>
      <Grid container spacing={4} justify={"space-between"}>
        <Grid item sm={12} md={6}>
          <Box container alignItems="center" mb={4}>
            <Typography tag="p" variant="Subtitle2">
              {intl.formatMessage({
                id: "projects.finance.servicecostperyear",
                defaultMessage: "Service cost per year",
              })}
            </Typography>
            <Tooltip
              toolTipText={intl.formatMessage({
                id: "project.maintenanceInfo.serviceCostPerYearTooltip",
                defaultMessage:
                  "Based on a value or a percentage of the total equipment investment cost",
              })}
            >
              <IconButton
                mx={2}
                icon="info"
                size="Medium"
                disabled={disabled}
              />
            </Tooltip>
          </Box>
        </Grid>
        <Grid item sm={12} md={6}>
          <Box justifyContent="flex-end">
            <Toggle
              options={[
                { label: currency, value: AmountUnit.Currency },
                { label: "Percent %", value: AmountUnit.Percent },
              ]}
              value={unit}
              onChange={(val) => {
                setUnit(val);
                setValue(unitFieldPath as any, val, { shouldDirty: true });
                onUnitChange?.(val);
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={8}>
        {[...Array(8)].map((_, index) => (
          <Grid item sm={12} md={6} lg={6} key={index} alignItems="center">
            <Typography tag="p" variant="Body2" className="tcsp-medium" pb={2}>
              {intl.formatMessage(
                {
                  id: "general.year",
                  defaultMessage: `Year {number}`,
                },
                { number: index + 1 },
              )}
            </Typography>

            <FormTextWithSuffix
              placeholder={intl.formatMessage({
                id: "general.amount",
                defaultMessage: "Amount",
              })}
              suffix={unit === AmountUnit.Percent ? "%" : currency}
              fitContent={false}
              disabled={disabled}
              {...register(
                `${costsFieldPath}.${index}` as any,
                getFormFieldPropertiesForServiceCost(),
              )}
              error={(errorsArray?.[index] as FieldError)?.message}
              allowDecimals={false}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default ServiceYearCosts;
